import type { Match3 } from '.';
import { action } from '@play-co/replicant';
import { defineActions } from '../../utils';

export const match3Actions = (
  Match3: Pick<Match3, 'ruleset' | 'schema' | 'getters' | 'modifiers'>,
  { Inventory }: Match3['$DEPENDENCIES'],
) =>
  defineActions<Match3>()({
    match3ResetBoard: action((state, _: void, { math: { random } }) => {
      state.match3.board = Match3.ruleset.getInitialBoardState(random);
      state.match3.turns = [];
      delete state.match3.currentMatchIndex;
      Match3.modifiers(state).beginNewTurn();
    }),

    // TODO Validate distance to prevent cheating
    match3SwapBlocks: action((state, { a, b }: Match3.PositionSwap) => {
      const { getBlockType } = Match3.getters(state);
      const { setBlockType } = Match3.modifiers(state);
      const aType = getBlockType(a);
      const bType = getBlockType(b);
      setBlockType(a, bType);
      setBlockType(b, aType);
    }),

    match3ReplaceMatches: action((state, _: void, api) => {
      const { getMatchSets, getBlockType } = Match3.getters(state);
      const { replaceMatches } = Match3.modifiers(state);

      const sets = getMatchSets();
      const matches = sets.map((positions) => {
        const type = getBlockType(positions[0]);
        return {
          type,
          count: positions.length,
        };
      });

      if (matches.length) {
        const currentTurn = [...state.match3.turns].pop();
        if (!currentTurn.matches?.length) {
          // This is the first match this turn, so set the initial match index
          state.match3.currentMatchIndex = 0;
        }
        currentTurn.matches.push(...matches);

        replaceMatches(sets.flat(), () => api.math.random());
      }

      return sets;
    }),

    match3ConsumeMatchReward: action((state, _: void) => {
      const { rewards } = Match3.ruleset;
      const { getCurrentMatch } = Match3.getters(state);
      const currentMatch = getCurrentMatch();
      if (!currentMatch) {
        return { rewardType: 'points', amount: 0 } as const;
      }

      const { type: blockType, count } = currentMatch;
      const { type: rewardType, base, multiplier } = rewards[blockType];

      const amount = base * multiplier(count);
      const turn = [...state.match3.turns].pop();
      turn.rewards[rewardType] += amount;

      // Move index to next match to prevent replay exploit
      state.match3.currentMatchIndex += 1;

      return { rewardType, amount };
    }),

    match3EndTurn: action((state, _: void) => {
      const comboMultiplier = Match3.getters(state).getComboMultiplier();
      const currentTurn = [...state.match3.turns].pop();
      if (currentTurn) {
        currentTurn.rewards.points *= comboMultiplier;
      }

      Match3.modifiers(state).beginNewTurn();
    }),

    match3ConsumeTotalReward: action((state, _: void, api) => {
      const { points, gems } = Match3.getters(state).getTotalRewards();
      const { coins } = Match3.ruleset.getRewardAmounts(points);

      const now = api.date.now();
      const oldCurrencies = Inventory.getters(state).getCurrencies(now);
      Inventory.modifiers(state).addPoints(coins);
      Inventory.modifiers(state).addFreeGems(gems);

      api.sendAnalyticsEvents([
        Inventory.ruleset.getCurrencyGrantEvent(
          {
            feature: 'minigame_reward',
            $subFeature: 'match3',
          },
          {
            old: oldCurrencies,
            new: Inventory.getters(state).getCurrencies(now),
          },
        ),
      ]);

      state.match3.turns = [];
      delete state.match3.currentMatchIndex;
    }),

    // CHEATS
    ...(process.env.IS_DEVELOPMENT
      ? {
          cheat_match3SetBoardState: action(
            (state, board: Match3.BoardState) => {
              state.match3.board = board;
            },
          ),
        }
      : {}),
  });
